import React, { useCallback, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { IconButton, LinkIconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import DepartmentIcon from '@atlaskit/icon/core/department';
import { Inline } from '@atlaskit/primitives/compiled';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';

import { getIconForExternalMessageIntegration } from '../utils/externalMessageIntegrationIcons';
import { resolveExternalMessageFromIntegrations } from '../utils/thirdPartyIntegrationsByProduct';

const messages = defineMessages({
	viewOrgChart: {
		id: 'user-inline-preview.action-buttons.view-org-chart',
		defaultMessage: 'View org chart for {name}',
		description: 'Accessible label for the org chart icon button',
	},
	sendMessage: {
		id: 'user-inline-preview.action-buttons.send-message',
		defaultMessage: 'Send message to {name}',
		description: 'Accessible label for the send message button (e.g. Slack or Microsoft Teams)',
	},
	viewProfile: {
		id: 'user-inline-preview.action-buttons.view-profile',
		defaultMessage: 'View profile for {name}',
		description: 'Accessible label for the navigate to profile button',
	},
});

const styles = cssMap({
	actions: {
		opacity: 0,
	},
	actionsVisible: {
		opacity: 1,
	},
});

export interface ActionButtonsProps {
	name: string;
	accountId: string;
	email?: string | null;
	isVisible: boolean;
	cloudId: string;
	onViewOrgChart?: () => void;
	thirdPartyIntegrations?: {
		externalId: string;
		integrationId: string;
		product: string;
	}[];
}

/**
 * Internal component that renders action buttons (3p message, org chart, view profile).
 * Shows each button when the corresponding data and handler are available.
 */
function ActionButtons({
	name,
	accountId,
	email,
	isVisible,
	cloudId,
	onViewOrgChart,
	thirdPartyIntegrations,
}: ActionButtonsProps): React.JSX.Element {
	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const { href, target, onNavigate } = navigateToTeamsApp({
		type: 'USER',
		payload: { userId: accountId },
		cloudId: cloudId ?? '',
	});

	const externalMessage = useMemo(
		() => resolveExternalMessageFromIntegrations(thirdPartyIntegrations, email),
		[email, thirdPartyIntegrations],
	);

	const handleThirdPartyMessageClick = useCallback(() => {
		fireEvent('ui.userInlinePreview.clicked.thirdPartyMessage', {
			integration: externalMessage?.entry.integrationId ?? '',
		});
	}, [fireEvent, externalMessage]);

	const handleOrgChartClick = useCallback(() => {
		fireEvent('ui.userInlinePreview.clicked.reportingLines');
		onViewOrgChart?.();
	}, [fireEvent, onViewOrgChart]);

	const handleViewProfileClick = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
			fireEvent('ui.userInlinePreview.clicked.viewProfile');
			onNavigate?.(event);
		},
		[fireEvent, onNavigate],
	);

	/** When actions are visually hidden, keep them out of the tab order so `aria-hidden` does not trap focus (axe aria-hidden-focus). */
	const tabIndexWhenHidden = isVisible ? undefined : -1;

	// TODO update org chart to use the TeamsLinkIconButton (once available) — see UserProfilePreview ActionBar
	return (
		<Inline
			space="space.100"
			alignBlock="center"
			aria-hidden={!isVisible}
			xcss={cx(styles.actions, isVisible && styles.actionsVisible)}
		>
			{externalMessage && (
				<LinkIconButton
					tabIndex={tabIndexWhenHidden}
					href={externalMessage.dm.openDmUrl}
					target="_blank"
					rel="noopener noreferrer"
					onClick={handleThirdPartyMessageClick}
					icon={getIconForExternalMessageIntegration(externalMessage.dm.integration)}
					label={intl.formatMessage(messages.sendMessage, { name })}
				/>
			)}
			{accountId && onViewOrgChart && (
				<IconButton
					tabIndex={tabIndexWhenHidden}
					onClick={handleOrgChartClick}
					icon={DepartmentIcon}
					label={intl.formatMessage(messages.viewOrgChart, { name })}
				/>
			)}
			{accountId && href && (
				<LinkIconButton
					tabIndex={tabIndexWhenHidden}
					href={href}
					target={target}
					onClick={handleViewProfileClick}
					icon={ArrowRightIcon}
					label={intl.formatMessage(messages.viewProfile, { name })}
					rel={target === '_blank' ? 'noopener noreferrer' : undefined}
				/>
			)}
		</Inline>
	);
}

export { ActionButtons };
